import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import s from './TabelasPreco.module.css';
import useNavegacaoLista from '../hooks/useNavegacaoLista';

/* ────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────── */
const fmt2 = v => {
  const n = parseFloat(v);
  return isNaN(n) ? '0.00' : n.toFixed(2);
};

const calcPrecoTabela = (precoVenda, perc) => {
  const p = parseFloat(precoVenda) || 0;
  const pct = parseFloat(perc);
  if (isNaN(pct)) return fmt2(p);
  return fmt2(p * (1 + pct / 100));
};

const calcPerc = (precoVenda, precoTabela) => {
  const pv = parseFloat(precoVenda) || 0;
  const pt = parseFloat(precoTabela);
  if (!pv || isNaN(pt)) return '0.0000';
  return ((pt / pv - 1) * 100).toFixed(4);
};

/* ────────────────────────────────────────────────────────
   Estado vazio
   ──────────────────────────────────────────────────────── */
const FORM_VAZIO = {
  descricao: '',
  tipo_ajuste: 'percentual',
  valor_ajuste: '',
  base_calculo: 'venda',
  obs: '',
  produtos: [],
  clientes: [],
  incluir_produtos_novos: false,
};

/* ────────────────────────────────────────────────────────
   Mapeamento API → form
   ──────────────────────────────────────────────────────── */
const tabelaParaForm = t => ({
  descricao:   t.descricao   ?? '',
  tipo_ajuste: t.tipo_ajuste ?? 'percentual',
  valor_ajuste: t.valor_ajuste != null ? String(t.valor_ajuste) : '',
  base_calculo: t.base_calculo ?? 'venda',
  obs:         t.obs         ?? '',
  produtos: (t.produtos || []).map(p => ({
    produto_id:    p.produto_id,
    codigo_interno: p.codigo_interno,
    nome:          p.nome,
    preco_venda:   p.preco_venda,
    preco_custo:   p.preco_custo,
    unidade:       p.unidade,
    percentual:    p.percentual != null ? String(p.percentual) : '',
    preco_fixo:    p.preco_fixo != null ? String(p.preco_fixo) : '',
  })),
  clientes: t.clientes || [],
  incluir_produtos_novos: !!t.incluir_produtos_novos,
});

/* ────────────────────────────────────────────────────────
   Modal — Buscar tabela
   ──────────────────────────────────────────────────────── */
function ModalBuscaTabela({ onSelecionar, onFechar }) {
  const [busca, setBusca] = useState('');
  const [selecionado, setSelecionado] = useState(null);

  const { data: lista = [], isFetching } = useQuery({
    queryKey: ['tabelas-preco-busca', busca],
    queryFn: () => api.get('/tabelas-preco', { params: { busca } }).then(r => r.data),
    keepPreviousData: true,
  });
  const refLinha = useNavegacaoLista({
    lista, indice: lista.findIndex(t => t.id === selecionado?.id), aoMudar: setSelecionado, aoEnter: onSelecionar,
  });

  return (
    <div className={s.overlay}>
      <div className={s.modal} style={{ width: 520 }}>
        <div className={s.modalTitle}>Buscar Tabela de Preço</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow}>
            <input
              className={s.inputErp}
              style={{ flex: 1 }}
              placeholder="Código ou descrição..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              autoFocus
            />
            {isFetching && <span style={{ fontSize: 12, color: '#888' }}>buscando...</span>}
          </div>
          <table className={s.modalTabela}>
            <thead>
              <tr>
                <th style={{ width: 60 }}>Código</th>
                <th>Descrição</th>
                <th style={{ width: 90 }}>Tipo Ajuste</th>
                <th style={{ width: 80 }}>Produtos</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(t => (
                <tr
                  key={t.id}
                  ref={selecionado?.id === t.id ? refLinha : null}
                  className={selecionado?.id === t.id ? s.selecionado : ''}
                  onClick={() => setSelecionado(t)}
                  onDoubleClick={() => onSelecionar(t)}
                >
                  <td>{t.codigo}</td>
                  <td>{t.descricao}</td>
                  <td>
                    {t.tipo_ajuste === 'percentual'
                      ? `Percentual${t.base_calculo === 'custo' ? ' (custo)' : ''}`
                      : 'Preço Fixo'}
                  </td>
                  <td style={{ textAlign: 'center' }}>{t.total_produtos}</td>
                </tr>
              ))}
              {!lista.length && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: 10 }}>
                  Nenhuma tabela encontrada
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => selecionado && onSelecionar(selecionado)} disabled={!selecionado}>Selecionar</button>
          <button className={s.btnErp} onClick={onFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Modal — Buscar produto para adicionar
   ──────────────────────────────────────────────────────── */
function ModalBuscaProduto({ produtosJaAdicionados, onAdicionar, onFechar }) {
  const [busca, setBusca] = useState('');
  const [selecionado, setSelecionado] = useState(null);

  const { data: lista = [], isFetching } = useQuery({
    queryKey: ['produtos-busca-tabela', busca],
    queryFn: () => api.get('/produtos', { params: { busca, limite: 50 } }).then(r => r.data?.items ?? r.data),
    keepPreviousData: true,
  });

  const disponiveis = lista.filter(p => !produtosJaAdicionados.includes(p.id));
  const refLinha = useNavegacaoLista({
    lista: disponiveis, indice: disponiveis.findIndex(p => p.id === selecionado?.id), aoMudar: setSelecionado, aoEnter: onAdicionar,
  });

  return (
    <div className={s.overlay}>
      <div className={s.modal} style={{ width: 620 }}>
        <div className={s.modalTitle}>Adicionar Produto à Tabela</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow}>
            <input
              className={s.inputErp}
              style={{ flex: 1 }}
              placeholder="Código, nome ou referência..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
              autoFocus
            />
            {isFetching && <span style={{ fontSize: 12, color: '#888' }}>buscando...</span>}
          </div>
          <table className={s.modalTabela}>
            <thead>
              <tr>
                <th style={{ width: 70 }}>Código</th>
                <th>Nome</th>
                <th style={{ width: 60 }}>Unid.</th>
                <th style={{ width: 90 }}>Preço Venda</th>
              </tr>
            </thead>
            <tbody>
              {disponiveis.map(p => (
                <tr
                  key={p.id}
                  ref={selecionado?.id === p.id ? refLinha : null}
                  className={selecionado?.id === p.id ? s.selecionado : ''}
                  onClick={() => setSelecionado(p)}
                  onDoubleClick={() => onAdicionar(p)}
                >
                  <td>{p.codigo_interno}</td>
                  <td>{p.nome}</td>
                  <td>{p.unidade}</td>
                  <td style={{ textAlign: 'right' }}>
                    {parseFloat(p.preco_venda || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
              {!disponiveis.length && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: 10 }}>
                  Nenhum produto encontrado
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => selecionado && onAdicionar(selecionado)} disabled={!selecionado}>Adicionar</button>
          <button className={s.btnErp} onClick={onFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Modal — Inserir produtos em massa (todos / grupo / subgrupo / fornecedor)
   ──────────────────────────────────────────────────────── */
function ModalInserirEmMassa({ produtosJaAdicionados, onAdicionar, onFechar }) {
  const [modo, setModo]             = useState('todos'); // 'todos' | 'grupo' | 'subgrupo' | 'fornecedor'
  const [grupoId, setGrupoId]       = useState('');
  const [subgrupoId, setSubgrupoId] = useState('');
  const [fornecedorId, setFornecedorId] = useState('');
  const [buscando, setBuscando]     = useState(false);
  const [prontos, setProntos]       = useState(null); // null = ainda não buscou

  const { data: grupos = [] } = useQuery({
    queryKey: ['grupos-lista-massa'],
    queryFn: () => api.get('/grupos').then(r => r.data),
  });
  const { data: subgrupos = [] } = useQuery({
    queryKey: ['subgrupos-lista-massa'],
    queryFn: () => api.get('/subgrupos').then(r => r.data),
  });
  const { data: fornecedoresLista = [] } = useQuery({
    queryKey: ['fornecedores-lista-massa'],
    queryFn: () => api.get('/fornecedores').then(r => r.data),
  });

  const trocarModo = novoModo => { setModo(novoModo); setProntos(null); };

  const podeBuscar =
    modo === 'todos' ||
    (modo === 'grupo' && grupoId) ||
    (modo === 'subgrupo' && subgrupoId) ||
    (modo === 'fornecedor' && fornecedorId);

  const buscar = async () => {
    setBuscando(true);
    try {
      const params = { todos: 'true' };
      if (modo === 'grupo')      params.grupo_id      = grupoId;
      if (modo === 'subgrupo')   params.subgrupo_id    = subgrupoId;
      if (modo === 'fornecedor') params.fornecedor_id  = fornecedorId;
      const { data } = await api.get('/produtos', { params });
      setProntos((data || []).filter(p => !produtosJaAdicionados.includes(p.id)));
    } catch {
      setProntos([]);
    } finally {
      setBuscando(false);
    }
  };

  return (
    <div className={s.overlay}>
      <div className={s.modal} style={{ width: 460 }}>
        <div className={s.modalTitle}>Inserir Produtos em Massa</div>
        <div className={s.modalBody}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
            <label className={s.radioLabel}>
              <input type="radio" name="modoMassa" checked={modo === 'todos'}
                onChange={() => trocarModo('todos')} />
              Todos os produtos
            </label>
            <label className={s.radioLabel}>
              <input type="radio" name="modoMassa" checked={modo === 'grupo'}
                onChange={() => trocarModo('grupo')} />
              Por Grupo
            </label>
            {modo === 'grupo' && (
              <select className={s.selectErp} style={{ marginLeft: 22 }}
                value={grupoId} onChange={e => { setGrupoId(e.target.value); setProntos(null); }}>
                <option value="">— selecione o grupo —</option>
                {grupos.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
              </select>
            )}
            <label className={s.radioLabel}>
              <input type="radio" name="modoMassa" checked={modo === 'subgrupo'}
                onChange={() => trocarModo('subgrupo')} />
              Por Subgrupo
            </label>
            {modo === 'subgrupo' && (
              <select className={s.selectErp} style={{ marginLeft: 22 }}
                value={subgrupoId} onChange={e => { setSubgrupoId(e.target.value); setProntos(null); }}>
                <option value="">— selecione o subgrupo —</option>
                {subgrupos.map(sg => (
                  <option key={sg.id} value={sg.id}>
                    {sg.nome}{sg.grupo_nome ? ` (${sg.grupo_nome})` : ''}
                  </option>
                ))}
              </select>
            )}
            <label className={s.radioLabel}>
              <input type="radio" name="modoMassa" checked={modo === 'fornecedor'}
                onChange={() => trocarModo('fornecedor')} />
              Por Fornecedor
            </label>
            {modo === 'fornecedor' && (
              <select className={s.selectErp} style={{ marginLeft: 22 }}
                value={fornecedorId} onChange={e => { setFornecedorId(e.target.value); setProntos(null); }}>
                <option value="">— selecione o fornecedor —</option>
                {fornecedoresLista.map(f => <option key={f.id} value={f.id}>{f.nome_fantasia}</option>)}
              </select>
            )}
          </div>

          <button className={s.btnErp} onClick={buscar} disabled={!podeBuscar || buscando}>
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>

          {prontos !== null && (
            <div style={{ marginTop: 10, fontSize: 12.5 }}>
              {prontos.length === 0
                ? <span style={{ color: '#888' }}>Nenhum produto novo encontrado (os demais já estão nesta tabela).</span>
                : <span><strong>{prontos.length}</strong> produto(s) serão adicionados.</span>}
            </div>
          )}
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={() => onAdicionar(prontos)} disabled={!prontos?.length}>
            Adicionar{prontos?.length ? ` (${prontos.length})` : ''}
          </button>
          <button className={s.btnErp} onClick={onFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Modal — Aplicar desconto/acréscimo para todos
   ──────────────────────────────────────────────────────── */
function ModalAplicar({ tipoAjuste, baseCalculo = 'venda', onAplicar, onFechar }) {
  const [tipo, setTipo]   = useState(tipoAjuste === 'fixo' ? 'fixo' : 'percentual');
  const [valor, setValor] = useState('');

  const handleAplicar = () => {
    const v = parseFloat(valor);
    if (isNaN(v)) { alert('Informe um valor válido.'); return; }
    onAplicar(tipo, v);
  };

  return (
    <div className={s.overlay}>
      <div className={s.modal} style={{ width: 360 }}>
        <div className={s.modalTitle}>Aplicar Desconto / Acréscimo para Todos</div>
        <div className={s.modalBody}>
          <div style={{ marginBottom: 10 }}>
            <label className={s.radioLabel} style={{ marginBottom: 6 }}>
              <input type="radio" name="tipoApl" value="percentual"
                checked={tipo === 'percentual'} onChange={() => setTipo('percentual')} />
              Percentual (%) — positivo=acréscimo, negativo=desconto
            </label>
            <label className={s.radioLabel}>
              <input type="radio" name="tipoApl" value="fixo"
                checked={tipo === 'fixo'} onChange={() => setTipo('fixo')} />
              Preço Fixo (R$) — mesmo valor para todos
            </label>
          </div>
          <div className={s.campo}>
            <span className={s.campoLabel}>{tipo === 'percentual' ? 'Percentual (%)' : 'Preço Fixo (R$)'}</span>
            <input
              className={s.inputErp}
              style={{ width: 120 }}
              type="number"
              step="0.01"
              value={valor}
              onChange={e => setValor(e.target.value)}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleAplicar()}
            />
          </div>
          <div style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>
            {tipo === 'percentual'
              ? `Ex: 10 = +10%, -5 = -5% sobre o preço de ${baseCalculo === 'custo' ? 'custo' : 'venda'} de cada produto (base definida acima, em "Base de cálculo do percentual").`
              : 'Define o mesmo preço fixo para todos os produtos da tabela.'}
          </div>
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={handleAplicar}>Aplicar</button>
          <button className={s.btnErp} onClick={onFechar}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Sub-aba Produtos
   ──────────────────────────────────────────────────────── */
function AbaProdutos({ form, setForm, editando }) {
  const [linhaSel, setLinhaSel]         = useState(null);
  const [modalProduto, setModalProduto] = useState(false);
  const [modalMassa, setModalMassa]     = useState(false);
  const [filtro, setFiltro]             = useState('');
  const baseCalculo = form.base_calculo === 'custo' ? 'custo' : 'venda';
  const campoBase = baseCalculo === 'custo' ? 'preco_custo' : 'preco_venda';

  const atualizarProd = (idx, campo, valor) => {
    setForm(f => {
      const prods = f.produtos.map((p, i) => {
        if (i !== idx) return p;
        const updated = { ...p, [campo]: valor };
        const base = parseFloat(p[campoBase]) || 0;
        if (campo === 'percentual') {
          updated.preco_fixo = '';
        } else if (campo === 'preco_fixo') {
          updated.percentual = base ? calcPerc(base, valor) : '';
        }
        return updated;
      });
      return { ...f, produtos: prods };
    });
  };

  const removerProd = () => {
    if (linhaSel === null) return;
    setForm(f => ({
      ...f,
      produtos: f.produtos.filter((_, i) => i !== linhaSel),
    }));
    setLinhaSel(null);
  };

  const adicionarProd = prod => {
    setForm(f => ({
      ...f,
      produtos: [...f.produtos, {
        produto_id:     prod.id,
        codigo_interno: prod.codigo_interno,
        nome:           prod.nome,
        preco_venda:    prod.preco_venda,
        preco_custo:    prod.preco_custo,
        unidade:        prod.unidade,
        percentual:     '',
        preco_fixo:     '',
      }],
    }));
    setModalProduto(false);
  };

  const adicionarVarios = produtos => {
    setForm(f => ({
      ...f,
      produtos: [...f.produtos, ...produtos.map(prod => ({
        produto_id:     prod.id,
        codigo_interno: prod.codigo_interno,
        nome:           prod.nome,
        preco_venda:    prod.preco_venda,
        preco_custo:    prod.preco_custo,
        unidade:        prod.unidade,
        percentual:     '',
        preco_fixo:     '',
      }))],
    }));
    setModalMassa(false);
  };

  const idsAdicionados = form.produtos.map(p => p.produto_id);

  // Filtro por nome ou código — a tabela pode ter milhares de produtos
  // (ex.: "incluir produtos novos automaticamente"), precisa achar um item
  // rápido sem rolar a lista inteira. Mantém o índice ORIGINAL de cada linha
  // (_idx) pra editar/remover continuar batendo com form.produtos mesmo com
  // a lista filtrada.
  const filtroNorm = filtro.trim().toLowerCase();
  const produtosComIdx = form.produtos.map((p, i) => ({ ...p, _idx: i }));
  const produtosFiltrados = filtroNorm
    ? produtosComIdx.filter(p =>
        String(p.codigo_interno ?? '').toLowerCase().includes(filtroNorm) ||
        (p.nome ?? '').toLowerCase().includes(filtroNorm))
    : produtosComIdx;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <input
          className={s.inputTabela}
          style={{ flex: 1, maxWidth: 320 }}
          placeholder="Buscar por nome ou código…"
          value={filtro}
          onChange={e => setFiltro(e.target.value)}
        />
        {filtro && (
          <button className={s.btnErp} onClick={() => setFiltro('')}>Limpar busca</button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, border: '1px solid #d7dde5' }}>
        <table className={s.tabelaProdutos}>
          <colgroup>
            <col style={{ width: 70 }} />
            <col />
            <col style={{ width: 60 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 110 }} />
          </colgroup>
          <thead>
            <tr>
              <th>Código</th>
              <th>Nome / Descrição</th>
              <th>Unid.</th>
              <th style={{ textAlign: 'right' }}>
                {baseCalculo === 'custo' ? 'Preço Custo (base)' : 'Preço Venda (base)'}
              </th>
              <th style={{ textAlign: 'right' }}>(-/+)%</th>
              <th style={{ textAlign: 'right' }}>Preço na Tabela R$</th>
            </tr>
          </thead>
          <tbody>
            {produtosFiltrados.map(p => {
              const i = p._idx;
              const base = parseFloat(p[campoBase]) || 0;
              const precoTabela = p.preco_fixo !== '' && p.preco_fixo != null
                ? fmt2(p.preco_fixo)
                : calcPrecoTabela(base, p.percentual);
              const percExibido = p.preco_fixo !== '' && p.preco_fixo != null
                ? calcPerc(base, p.preco_fixo)
                : (p.percentual !== '' ? p.percentual : '0.0000');

              return (
                <tr
                  key={p.produto_id}
                  className={linhaSel === i ? s.linhaSel : ''}
                  onClick={() => setLinhaSel(i)}
                >
                  <td>{p.codigo_interno}</td>
                  <td>{p.nome}</td>
                  <td>{p.unidade}</td>
                  <td style={{ textAlign: 'right' }}>{fmt2(base)}</td>
                  <td style={{ padding: '1px 4px' }}>
                    <input
                      className={s.inputTabela}
                      readOnly={!editando}
                      value={percExibido}
                      onChange={e => atualizarProd(i, 'percentual', e.target.value)}
                      onBlur={e => {
                        const v = parseFloat(e.target.value);
                        atualizarProd(i, 'percentual', isNaN(v) ? '0.0000' : v.toFixed(4));
                      }}
                      style={{ textAlign: 'right' }}
                      title="Positivo=acréscimo, negativo=desconto"
                    />
                  </td>
                  <td style={{ padding: '1px 4px' }}>
                    <input
                      className={s.inputTabela}
                      readOnly={!editando}
                      value={precoTabela}
                      onChange={e => atualizarProd(i, 'preco_fixo', e.target.value)}
                      onBlur={e => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v)) atualizarProd(i, 'preco_fixo', v.toFixed(2));
                      }}
                      style={{ textAlign: 'right' }}
                    />
                  </td>
                </tr>
              );
            })}
            {!form.produtos.length && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: 12 }}>
                  Nenhum produto vinculado. Clique em + para adicionar.
                </td>
              </tr>
            )}
            {!!form.produtos.length && !produtosFiltrados.length && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: 12 }}>
                  Nenhum produto encontrado para "{filtro}".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11.5, color: '#555', margin: '6px 0', fontWeight: 600 }}>
        {filtro
          ? `${produtosFiltrados.length} de ${form.produtos.length} produto(s) — filtrado por "${filtro}"`
          : `${form.produtos.length} produto(s) lançado(s) nesta tabela`}
      </div>

      {editando && (
        <div className={s.prodBotoes}>
          <button className={s.btnErp} onClick={() => setModalProduto(true)}>+ Produto</button>
          <button className={s.btnErp} onClick={() => setModalMassa(true)}>+ Vários... (todos/grupo/subgrupo/fornecedor)</button>
          <button className={s.btnErp} onClick={removerProd} disabled={linhaSel === null}>- Remover</button>
        </div>
      )}

      {modalProduto && (
        <ModalBuscaProduto
          produtosJaAdicionados={idsAdicionados}
          onAdicionar={adicionarProd}
          onFechar={() => setModalProduto(false)}
        />
      )}
      {modalMassa && (
        <ModalInserirEmMassa
          produtosJaAdicionados={idsAdicionados}
          onAdicionar={adicionarVarios}
          onFechar={() => setModalMassa(false)}
        />
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Modal — Inserir clientes na tabela (busca por nome/código/CPF-CNPJ)
   ──────────────────────────────────────────────────────── */
function ModalInserirClientes({ tabelaId, clientesJaAdicionados, onInserido, onFechar }) {
  const [busca, setBusca] = useState('');
  const [inserindoId, setInserindoId] = useState(null);
  const [inseridosAgora, setInseridosAgora] = useState(new Set());

  const buscaOk = busca.trim().length >= 2;

  const { data: lista = [], isFetching } = useQuery({
    queryKey: ['clientes-busca-tabela', busca],
    queryFn: () => api.get('/clientes', { params: { busca, limite: 30 } }).then(r => r.data),
    enabled: buscaOk,
    keepPreviousData: true,
  });

  const disponiveis = lista.filter(c => !clientesJaAdicionados.includes(c.id) && !inseridosAgora.has(c.id));

  const inserir = async cliente => {
    setInserindoId(cliente.id);
    try {
      await api.post(`/tabelas-preco/${tabelaId}/clientes`, { cliente_ids: [cliente.id] });
      setInseridosAgora(prev => new Set(prev).add(cliente.id));
      onInserido(cliente);
    } catch {
      alert('Erro ao inserir cliente na tabela.');
    } finally {
      setInserindoId(null);
    }
  };

  return (
    <div className={s.overlay}>
      <div className={s.modal} style={{ width: 760 }}>
        <div className={s.modalTitle}>Inserir Clientes na Tabela</div>
        <div className={s.modalBody}>
          <div className={s.modalSearchRow}>
            <input
              className={s.inputErp}
              style={{ flex: 1 }}
              placeholder="Nome, código ou CPF/CNPJ... (ex: PREFEITURA) — mín. 2 letras"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              autoFocus
            />
            {isFetching && <span style={{ fontSize: 12, color: '#888' }}>buscando...</span>}
          </div>
          <table className={s.modalTabela}>
            <thead>
              <tr>
                <th style={{ width: 70 }}>Código</th>
                <th style={{ minWidth: 340 }}>Nome</th>
                <th style={{ width: 130 }}>CPF/CNPJ</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {!buscaOk && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: 10 }}>
                  Digite ao menos 2 letras pra buscar.
                </td></tr>
              )}
              {buscaOk && !isFetching && disponiveis.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: 10 }}>
                  Nenhum cliente encontrado (ou já inserido).
                </td></tr>
              )}
              {disponiveis.map(c => (
                <tr key={c.id}>
                  <td>{c.codigo}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{c.nome}</td>
                  <td>{c.cpf_cnpj || '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button className={s.btnErp} disabled={inserindoId === c.id} onClick={() => inserir(c)}>
                      {inserindoId === c.id ? '...' : '+ Inserir'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {inseridosAgora.size > 0 && (
            <div style={{ fontSize: 12, color: '#2e7d32', marginTop: 8 }}>
              {inseridosAgora.size} cliente(s) inserido(s) nesta sessão — pode continuar buscando.
            </div>
          )}
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Sub-aba Clientes
   ──────────────────────────────────────────────────────── */
function AbaClientes({ form, tabelaId, onClientesAtualizados }) {
  const [modalInserir, setModalInserir] = useState(false);
  const [linhaSel, setLinhaSel]         = useState(null);
  const [removendo, setRemovendo]       = useState(false);

  const idsAdicionados = form.clientes.map(c => c.cliente_id);

  const removerCliente = async () => {
    if (linhaSel === null) return;
    const c = form.clientes[linhaSel];
    setRemovendo(true);
    try {
      await api.delete(`/tabelas-preco/${tabelaId}/clientes/${c.cliente_id}`);
      onClientesAtualizados(form.clientes.filter((_, i) => i !== linhaSel));
      setLinhaSel(null);
    } catch {
      alert('Erro ao remover cliente da tabela.');
    } finally {
      setRemovendo(false);
    }
  };

  return (
    <div>
      <table className={s.tabelaClientes}>
        <thead>
          <tr>
            <th style={{ width: 80 }}>Cód. Cliente</th>
            <th>Nome do Cliente</th>
          </tr>
        </thead>
        <tbody>
          {form.clientes.map((c, i) => (
            <tr key={c.cliente_id}
              className={linhaSel === i ? s.linhaSel : ''}
              onClick={() => setLinhaSel(i)}
              style={{ cursor: 'pointer' }}>
              <td>{c.codigo}</td>
              <td>{c.nome ?? '—'}</td>
            </tr>
          ))}
          {!form.clientes.length && (
            <tr>
              <td colSpan={2} style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', padding: 12 }}>
                Nenhum cliente vinculado a esta tabela.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {tabelaId && (
        <div className={s.prodBotoes}>
          <button className={s.btnErp} onClick={() => setModalInserir(true)}>+ Inserir Clientes...</button>
          <button className={s.btnErp} onClick={removerCliente} disabled={linhaSel === null || removendo}>- Remover</button>
        </div>
      )}

      {modalInserir && (
        <ModalInserirClientes
          tabelaId={tabelaId}
          clientesJaAdicionados={idsAdicionados}
          onInserido={cliente => {
            onClientesAtualizados([...form.clientes, { cliente_id: cliente.id, codigo: cliente.codigo, nome: cliente.nome }]);
          }}
          onFechar={() => setModalInserir(false)}
        />
      )}

      <div style={{ fontSize: 11, color: '#888', marginTop: 6, fontStyle: 'italic' }}>
        {!tabelaId && 'Salve a tabela antes de vincular clientes. '}
        Também pode ser definido cliente a cliente em Cadastro de Clientes → campo "Tabela de Preço".
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────
   Componente principal
   ──────────────────────────────────────────────────────── */
export default function TabelasPreco() {
  const navigate      = useNavigate();
  const queryClient   = useQueryClient();

  const [modo, setModo]             = useState('nav');   // 'nav' | 'novo' | 'editar'
  const [tabelaAtual, setTabelaAtual] = useState(null);  // objeto completo da tabela
  const [form, setForm]             = useState(FORM_VAZIO);
  const [aba, setAba]               = useState('produtos');
  const [posLista, setPosLista]     = useState(0);       // índice na lista de ids

  const [modalBusca, setModalBusca]   = useState(false);
  const [modalAplicar, setModalAplicar] = useState(false);
  const [erro, setErro]             = useState('');

  /* lista de ids para navegação < > */
  const { data: lista = [] } = useQuery({
    queryKey: ['tabelas-preco-lista'],
    queryFn: () => api.get('/tabelas-preco').then(r => r.data),
  });

  /* Busca próximo código para 'novo' */
  const { data: proxCodigo } = useQuery({
    queryKey: ['tabelas-preco-proximo'],
    queryFn: () => api.get('/tabelas-preco/proximo-codigo').then(r => r.data.proximo),
    enabled: modo === 'novo',
  });

  /* Carrega tabela pelo id */
  const carregarTabela = useCallback(async (id, pos) => {
    try {
      const { data } = await api.get(`/tabelas-preco/${id}`);
      setTabelaAtual(data);
      setForm(tabelaParaForm(data));
      if (pos !== undefined) setPosLista(pos);
      setModo('nav');
      setErro('');
    } catch {
      setErro('Erro ao carregar tabela.');
    }
  }, []);

  /* Navegação < > */
  const irPara = useCallback(async idx => {
    if (!lista.length) return;
    const clamped = Math.max(0, Math.min(idx, lista.length - 1));
    await carregarTabela(lista[clamped].id, clamped);
  }, [lista, carregarTabela]);

  const handlePrimeiro = () => irPara(0);
  const handleAnterior = () => irPara(posLista - 1);
  const handleProximo  = () => irPara(posLista + 1);
  const handleUltimo   = () => irPara(lista.length - 1);

  /* Mutations */
  const mutCriar = useMutation({
    mutationFn: payload => api.post('/tabelas-preco', payload).then(r => r.data),
    onSuccess: async tabela => {
      await queryClient.invalidateQueries(['tabelas-preco-lista']);
      const novaLista = await api.get('/tabelas-preco').then(r => r.data);
      const idx = novaLista.findIndex(t => t.id === tabela.id);
      await carregarTabela(tabela.id, idx >= 0 ? idx : 0);
    },
    onError: () => setErro('Erro ao salvar tabela.'),
  });

  const mutAtualizar = useMutation({
    mutationFn: ({ id, payload }) => api.put(`/tabelas-preco/${id}`, payload).then(r => r.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries(['tabelas-preco-lista']);
      await carregarTabela(tabelaAtual.id, posLista);
    },
    onError: () => setErro('Erro ao atualizar tabela.'),
  });

  const mutExcluir = useMutation({
    mutationFn: id => api.delete(`/tabelas-preco/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries(['tabelas-preco-lista']);
      const novaLista = await api.get('/tabelas-preco').then(r => r.data);
      if (novaLista.length) {
        const idx = Math.min(posLista, novaLista.length - 1);
        await carregarTabela(novaLista[idx].id, idx);
      } else {
        setTabelaAtual(null);
        setForm(FORM_VAZIO);
        setModo('nav');
      }
    },
    onError: () => setErro('Erro ao excluir tabela.'),
  });

  /* Botões do rodapé */
  const handleNovo = () => {
    setTabelaAtual(null);
    setForm(FORM_VAZIO);
    setModo('novo');
    setErro('');
  };

  const handleAlterar = () => {
    if (!tabelaAtual) return;
    setModo('editar');
    setErro('');
  };

  const handleCancelar = () => {
    if (modo === 'novo') {
      setForm(FORM_VAZIO);
    } else if (tabelaAtual) {
      setForm(tabelaParaForm(tabelaAtual));
    }
    setModo(tabelaAtual ? 'nav' : 'nav');
    setErro('');
  };

  const handleExcluir = () => {
    if (!tabelaAtual) return;
    if (!window.confirm(`Excluir a tabela "${tabelaAtual.descricao}"?`)) return;
    mutExcluir.mutate(tabelaAtual.id);
  };

  const handleSalvar = () => {
    if (!form.descricao.trim()) { setErro('Informe a descrição da tabela.'); return; }
    const payload = {
      descricao:    form.descricao.trim(),
      tipo_ajuste:  form.tipo_ajuste,
      valor_ajuste: parseFloat(form.valor_ajuste) || 0,
      base_calculo: form.base_calculo === 'custo' ? 'custo' : 'venda',
      obs:          form.obs || null,
      incluir_produtos_novos: !!form.incluir_produtos_novos,
      produtos: form.produtos.map(p => ({
        produto_id: p.produto_id,
        percentual: p.preco_fixo !== '' && p.preco_fixo != null
          ? null
          : (parseFloat(p.percentual) || 0),
        preco_fixo: p.preco_fixo !== '' && p.preco_fixo != null
          ? parseFloat(p.preco_fixo)
          : null,
      })),
    };
    if (modo === 'novo') {
      mutCriar.mutate(payload);
    } else {
      mutAtualizar.mutate({ id: tabelaAtual.id, payload });
    }
  };

  /* Aplicar desconto/acréscimo para todos */
  const handleAplicarTodos = (tipo, valor) => {
    setForm(f => ({
      ...f,
      produtos: f.produtos.map(p => {
        if (tipo === 'fixo') {
          return { ...p, preco_fixo: valor.toFixed(2), percentual: '' };
        }
        return { ...p, percentual: valor.toFixed(4), preco_fixo: '' };
      }),
    }));
    setModalAplicar(false);
  };

  /* Selecionar tabela via modal busca */
  const handleSelecionarTabela = async tabela => {
    setModalBusca(false);
    const idx = lista.findIndex(t => t.id === tabela.id);
    await carregarTabela(tabela.id, idx >= 0 ? idx : 0);
  };

  const editando     = modo === 'novo' || modo === 'editar';
  const salvando     = mutCriar.isLoading || mutAtualizar.isLoading;
  const temTabela    = !!tabelaAtual;
  const codigoExibir = modo === 'novo'
    ? (proxCodigo ?? '...')
    : (tabelaAtual?.codigo ?? '—');

  // Exemplo ao vivo do "Base do percentual" — direção explícita com número
  // de verdade, não só o nome do campo (rótulo ambíguo já causou confusão
  // antes: campo de taxa/conversão sem exemplo deixa dúvida se é "sobre
  // venda" ou "sobre custo", "positivo é a favor de quem" etc.).
  const valorAjusteNum = parseFloat(form.valor_ajuste) || 0;
  const baseExemploNome = form.base_calculo === 'custo' ? 'custo' : 'venda';
  const exemploResultado = (100 * (1 + valorAjusteNum / 100)).toFixed(2);
  const exemploBaseCalculo =
    `Ex.: produto com preço de ${baseExemploNome} R$ 100,00 → ` +
    `${valorAjusteNum >= 0 ? '+' : ''}${valorAjusteNum}% = R$ ${exemploResultado} nesta tabela.`;

  return (
    <div style={{ padding: 10, height: '100%', boxSizing: 'border-box' }}>
      <div className={s.erpWrap}>

        {/* ── Barra de título ── */}
        <div className={s.titleBar}>
          <span>💰</span> Tabela de Preços
        </div>

        {/* ── Cabeçalho / campos principais ── */}
        <div className={s.headerRow}>
          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Código:</span>
            <input
              className={s.inputErp}
              style={{ width: 70 }}
              value={codigoExibir}
              readOnly
            />
          </div>

          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Descrição:</span>
            <input
              className={s.inputErp}
              style={{ width: 260 }}
              value={form.descricao}
              readOnly={!editando}
              onChange={e => setForm(f => ({ ...f, descricao: e.target.value.toUpperCase() }))}
              maxLength={100}
              placeholder={editando ? 'Ex: TABELA 1, ATACADO, VIP...' : ''}
            />
          </div>

          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Tipo de ajuste:</span>
            <label className={s.radioLabel}>
              <input
                type="radio"
                name="tipo_ajuste"
                value="percentual"
                checked={form.tipo_ajuste === 'percentual'}
                disabled={!editando}
                onChange={() => setForm(f => ({ ...f, tipo_ajuste: 'percentual' }))}
              />
              Percentual (%)
            </label>
            <label className={s.radioLabel}>
              <input
                type="radio"
                name="tipo_ajuste"
                value="fixo"
                checked={form.tipo_ajuste === 'fixo'}
                disabled={!editando}
                onChange={() => setForm(f => ({ ...f, tipo_ajuste: 'fixo' }))}
              />
              Fixo (R$)
            </label>
          </div>

          <div className={s.headerGroup}>
            <span className={s.headerLabel}>Valor ajuste:</span>
            <input
              className={s.inputErp}
              style={{ width: 90, textAlign: 'right' }}
              value={form.valor_ajuste}
              readOnly={!editando}
              onChange={e => setForm(f => ({ ...f, valor_ajuste: e.target.value }))}
              type="number"
              step="0.01"
            />
            <span style={{ fontSize: 12, color: '#555' }}>
              {form.tipo_ajuste === 'percentual' ? '%' : 'R$'}
            </span>
          </div>

          {form.tipo_ajuste === 'percentual' && (
            <div className={s.headerGroup}>
              <span className={s.headerLabel}>Base do percentual:</span>
              <label className={s.radioLabel}>
                <input
                  type="radio"
                  name="base_calculo"
                  value="venda"
                  checked={form.base_calculo !== 'custo'}
                  disabled={!editando}
                  onChange={() => setForm(f => ({ ...f, base_calculo: 'venda' }))}
                />
                % sobre o preço de VENDA
              </label>
              <label className={s.radioLabel}>
                <input
                  type="radio"
                  name="base_calculo"
                  value="custo"
                  checked={form.base_calculo === 'custo'}
                  disabled={!editando}
                  onChange={() => setForm(f => ({ ...f, base_calculo: 'custo' }))}
                />
                % sobre o preço de CUSTO
              </label>
              <span style={{ fontSize: 11, color: '#666', fontStyle: 'italic', marginLeft: 8 }}>
                {exemploBaseCalculo}
              </span>
            </div>
          )}

          <div className={s.headerGroup}>
            <label className={s.radioLabel} title="Todo produto novo cadastrado entra automaticamente nesta tabela, sempre pelo percentual atual dela (se o percentual mudar depois, o produto acompanha)">
              <input
                type="checkbox"
                checked={form.incluir_produtos_novos}
                disabled={!editando}
                onChange={e => setForm(f => ({ ...f, incluir_produtos_novos: e.target.checked }))}
              />
              Incluir produtos novos automaticamente
            </label>
          </div>
        </div>

        {/* ── Abas ── */}
        <div className={s.tabBar}>
          {[
            { id: 'produtos',  label: 'Produtos' },
            { id: 'clientes', label: 'Clientes' },
            { id: 'obs',      label: 'Obs.' },
          ].map(a => (
            <button
              key={a.id}
              className={`${s.tab} ${aba === a.id ? s.tabAtiva : ''}`}
              onClick={() => setAba(a.id)}
            >
              {a.label}
            </button>
          ))}
        </div>

        {/* ── Conteúdo da aba ── */}
        <div className={s.tabContent}>
          {aba === 'produtos' && (
            <AbaProdutos form={form} setForm={setForm} editando={editando} />
          )}
          {aba === 'clientes' && (
            <AbaClientes
              form={form}
              tabelaId={tabelaAtual?.id}
              onClientesAtualizados={clientes => setForm(f => ({ ...f, clientes }))}
            />
          )}
          {aba === 'obs' && (
            <textarea
              className={s.textareaObs}
              readOnly={!editando}
              value={form.obs}
              onChange={e => setForm(f => ({ ...f, obs: e.target.value }))}
              placeholder={editando ? 'Observações sobre esta tabela de preços...' : ''}
            />
          )}
        </div>

        {/* ── Barra de status ── */}
        <div className={s.statusBar}>
          {erro
            ? <span style={{ color: '#c00', fontWeight: 'bold' }}>{erro}</span>
            : modo === 'novo'
            ? <span className={s.statusNovo}>NOVO REGISTRO</span>
            : modo === 'editar'
            ? <span className={s.statusEditar}>ALTERANDO: {tabelaAtual?.descricao}</span>
            : temTabela
            ? <span>Tabela: <strong>{tabelaAtual?.descricao}</strong> — {lista.length} tabela(s) cadastrada(s)</span>
            : <span>Nenhuma tabela carregada. Clique em Novo ou Buscar.</span>
          }
        </div>

        {/* ── Rodapé com botões ── */}
        <div className={s.footer}>
          <div className={s.footerLeft}>
            <button
              className={s.btnErp}
              onClick={() => setModalAplicar(true)}
              disabled={!editando || !form.produtos.length}
            >
              Aplicar desc./acréscimo...
            </button>
          </div>

          <div className={s.footerRight}>
            <button className={s.btnIcone} onClick={handlePrimeiro} disabled={editando || !lista.length} title="Primeiro">|◄</button>
            <button className={s.btnIcone} onClick={handleAnterior} disabled={editando || posLista <= 0} title="Anterior">◄</button>
            <span className={s.footerPos}>{temTabela ? `${posLista + 1}/${lista.length}` : '—'}</span>
            <button className={s.btnIcone} onClick={handleProximo}  disabled={editando || posLista >= lista.length - 1} title="Próximo">►</button>
            <button className={s.btnIcone} onClick={handleUltimo}   disabled={editando || !lista.length} title="Último">►|</button>
            <div className={s.footerSep} />
            <button className={s.btnErp} onClick={handleNovo}    disabled={editando}>Novo</button>
            <button className={s.btnErp} onClick={() => setModalBusca(true)} disabled={editando}>Buscar</button>
            <button className={s.btnErp} onClick={handleAlterar} disabled={editando || !temTabela}>Alterar</button>
            <button className={s.btnErp} onClick={handleCancelar} disabled={!editando}>Cancelar</button>
            <button className={s.btnErp} onClick={handleExcluir} disabled={editando || !temTabela}>Excluir</button>
            <button className={s.btnErp} onClick={handleSalvar}  disabled={!editando || salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
            <div className={s.footerSep} />
            <button className={s.btnErp} onClick={() => navigate('/dashboard')}>Sair</button>
          </div>
        </div>
      </div>

      {/* ── Modais ── */}
      {modalBusca && (
        <ModalBuscaTabela
          onSelecionar={handleSelecionarTabela}
          onFechar={() => setModalBusca(false)}
        />
      )}
      {modalAplicar && (
        <ModalAplicar
          tipoAjuste={form.tipo_ajuste}
          baseCalculo={form.base_calculo === 'custo' ? 'custo' : 'venda'}
          onAplicar={handleAplicarTodos}
          onFechar={() => setModalAplicar(false)}
        />
      )}
    </div>
  );
}
